import { format } from "date-fns";
import { X } from "lucide-react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ErrorMessage } from "@/app/components/error-message";
import { LoadingScreen } from "@/components/loading-screen";
import { SearchBar } from "@/components/search-bar";
import { useTableControl } from "@/components/table-control";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { OnboardingHeader } from "@/features/registration/components/onboarding-header";
import { SessionsTable } from "@/features/session/components/sessions-table";
import { useGetSessionsByOrg } from "@/features/sessions/api/get-sessions-by-org";
import { keepPreviousData } from "@tanstack/react-query";

export function SuperAdminSessionsPage() {
  const { pagination, setPagination, search, setSearch } = useTableControl();
  const [futureOnly, setFutureOnly] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const preselectedDay = searchParams.get("day");

  const {
    data: sessions,
    isLoading,
    isSuccess,
    isFetching,
    isError,
    isPlaceholderData,
    error,
  } = useGetSessionsByOrg(
    {
      pageSize: pagination.pageSize,
      pageIndex: pagination.pageIndex,
      search: search ?? undefined,
      futureOnly: futureOnly,
      day: preselectedDay ?? undefined,
    },
    { placeholderData: keepPreviousData },
  );

  const handleClearDay = () => {
    searchParams.delete("day");
    setSearchParams(searchParams);
  };

  if (isLoading) {
    return (
      <section>
        <OnboardingHeader
          title="Sessions"
          description="View sessions"
          className="mb-6"
        />
        <LoadingScreen />
      </section>
    );
  }

  if (isError || !isSuccess) {
    return (
      <div>
        <OnboardingHeader
          title="Sessions"
          description="View sessions"
          className="mb-6"
        />
        <ErrorMessage error={error} />
      </div>
    );
  }

  return (
    <section>
      <OnboardingHeader
        title="Sessions"
        description="View sessions"
        className="mb-6"
      />
      <div className="mb-2 flex flex-row gap-4">
        <SearchBar search={search} setSearch={setSearch} />
        {preselectedDay && (
          <Button
            variant="outline"
            className="px-6"
            type="button"
            onClick={handleClearDay}
          >
            {format(new Date(preselectedDay), "yyyy/MM/dd")}
            <span className="ml-2 rounded-full border border-border p-1">
              <X className="size-3" />
            </span>
          </Button>
        )}
        <div className="flex flex-row items-center gap-2">
          <Switch
            id="future-only"
            checked={futureOnly}
            onCheckedChange={setFutureOnly}
          />
          <Label htmlFor="future-only">Only show future sessions</Label>
        </div>
      </div>
      <SessionsTable
        data={sessions.items}
        pagination={pagination}
        pageCount={Math.ceil(sessions.count / pagination.pageSize)}
        onPaginationChange={setPagination}
        isLoading={isFetching && isPlaceholderData}
      />
    </section>
  );
}
